import { useState } from "react";
import { Pencil, AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const COUNTRIES = [
  "USA",
  "UK",
  "Australia",
  "New Zealand",
  "Canada",
  "Russia",
  "Georgia",
  "Kyrgyzstan",
  "Kazakhstan",
  "Uzbekistan",
  "Belarus",
  "Bangladesh",
  "Nepal",
  "Philippines",
  "China",
  "Egypt",
  "Other",
];

/**
 * Tiny modal that lets the counsellor / admin edit the lead's identity fields
 * (name, phone, email, preferred country). Mounted from the lead drawer header.
 *
 * Phone changes are destructive (they reset the WhatsApp 24h window and tag
 * historical messages under the old number) — we require a checkbox confirm.
 *
 * Backend audits every change as a `lead_details_edited` activity entry so the
 * audit trail stays clean.
 */
export default function EditLeadDetailsModal({ lead, open, onClose, onSaved }) {
  const [name, setName] = useState(lead?.name || "");
  const [phone, setPhone] = useState(lead?.phone || "");
  const [email, setEmail] = useState(lead?.email || "");
  const [country, setCountry] = useState(lead?.preferred_country || "");
  const [confirmPhone, setConfirmPhone] = useState(false);
  const [saving, setSaving] = useState(false);

  const phoneDirty = (phone || "").replace(/\D/g, "") !== (lead?.phone || "").replace(/\D/g, "");
  const dirty =
    name !== (lead?.name || "") ||
    phoneDirty ||
    email !== (lead?.email || "") ||
    country !== (lead?.preferred_country || "");

  const save = async () => {
    if (!lead) return;
    const body = {};
    if (name !== (lead.name || "")) body.name = name.trim();
    if (email !== (lead.email || "")) body.email = email.trim();
    if (country !== (lead.preferred_country || "")) body.preferred_country = country;
    if (phoneDirty) {
      body.phone = phone.replace(/\D/g, "");
      body.confirm_phone_change = true;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/leads/${lead.id}`, body);
      toast.success("Lead details updated");
      onSaved?.(data.lead);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="edit-lead-details-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0A2540]">
            <Pencil className="h-4 w-4" /> Edit lead details
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3.5">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Name
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1"
              data-testid="edit-lead-name"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Email
            </Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1"
              data-testid="edit-lead-email"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Preferred country
            </Label>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className="mt-1" data-testid="edit-lead-country">
                <SelectValue placeholder="Select…" />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Phone
            </Label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="mt-1 font-mono"
              data-testid="edit-lead-phone"
            />
            {phoneDirty && (
              <div
                className="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-[11px] text-amber-900"
                data-testid="edit-lead-phone-warning"
              >
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <div className="space-y-1.5">
                  <p className="font-medium">
                    Changing the phone is destructive
                  </p>
                  <p>
                    The WhatsApp 24-hour reply window resets to closed, and old
                    messages get tagged under the previous number so history
                    isn&apos;t lost.
                  </p>
                  <label className="mt-1 flex items-center gap-2 text-amber-900">
                    <Checkbox
                      checked={confirmPhone}
                      onCheckedChange={(v) => setConfirmPhone(!!v)}
                      data-testid="edit-lead-phone-confirm"
                    />
                    <span>I understand — proceed with phone change</span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={
              !dirty ||
              saving ||
              !name.trim() ||
              (phoneDirty && !confirmPhone)
            }
            className="bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
            data-testid="edit-lead-save"
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
